/*
 * -----------------------------------------------------------------------------
 *  Lucom Interaction Platform
 *  (C) Lucom GmbH, Erkrath. All rights reserved.
 * -----------------------------------------------------------------------------
 */

import Logger from "./Logger";

const log = new Logger(import.meta.url);

class SessionSync {
    network;
    eventBus;
    prolongationTimer: any;

    // id is optional
    constructor(network: any, eventBus: any) {
        this.network = network;
        this.eventBus = eventBus;

        this.prolongationTimer = undefined;
    }

    keepSessionAlive(firstProlongationInSeconds: number) {
        if (log.debugEnabled) log.debug(`keep the session alive`);

        this.triggerProlongation(firstProlongationInSeconds);
    }

    letSessionTimeout() {
        if (log.debugEnabled) log.debug(`let the session time out`);

        this.cancelProlongation();
    }

    // returns Promise that resolves when server answers
    terminateSession() {
        if (log.debugEnabled) log.debug(`terminate the session`);

        this.cancelProlongation();

        return this.network
            .delete("/shell/session", withCSRFToken())
            .then((response: any) => {
                const result = response.data;
                window.lucom.ip.setCSRFToken (undefined);
                this.eventBus.publish("shell.session", {
                    status: "terminated",
                });
                return Promise.resolve(result);
            })
            .catch((error: Error) => {
                log.error("failed to terminate session", error);
                this.eventBus.publish("shell.session", {
                    status: "unknown",
                    error: error,
                });
                return Promise.reject();
            });
    }

    // returns Promise that resolves to session info
    getSessionInfo() {
        return this.network
            .get("/shell/session")
            .then((response: any) => {
                if (response.status === 200) return Promise.resolve(response.data);
                else return Promise.resolve(undefined);
            })
            .catch((error: Error) => {
                log.error("failed to check session", error);
                return Promise.reject();
            });
    }

    // returns Promise that resolves when Locale was propagated to server
    setLocale(locale: string) {
        if (log.debugEnabled) log.debug(`set locale in session to "${locale}"`);

        if (typeof locale !== "string") {
            log.error(`invalid locale: ${locale}`);
            return Promise.reject(locale);
        }

        return this.network
            .post("/shell/locale", { locale }, withCSRFToken())
            .then(() => {
                return Promise.resolve(locale);
            })
            .catch((error: Error) => {
                log.error("failed to synchronize locale", error);
                return Promise.reject(locale);
            });
    }

    triggerProlongation(secondsFromNow: any) {
        this.cancelProlongation();

        this.prolongationTimer = window.setTimeout(() => {
            if (log.debugEnabled) log.debug(`send session prolongation request`);
            this.network
                .post("/shell/session/prolongation", {}, withCSRFToken())
                .then((response: any) => {
                    const result = response.data;
                    if (result.secondsToProlongation) {
                        this.triggerProlongation(result.secondsToProlongation);
                        this.eventBus.publish("shell.session.prolongation", {
                            status: "active",
                            age: result.age,
                            secondsToExpiration: result.secondsToExpiration,
                            secondsToProlongation: result.secondsToProlongation,
                            secondsToForcedTermination: result.secondsToForcedTermination,
                        });
                    } else {
                        log.warn("session will time out, prolongation stopped!");
                        this.eventBus.publish("shell.session.prolongation", {
                            status: "stopped",
                            reason: "forcedTermination",
                            secondsToForcedTermination: result.secondsToForcedTermination,
                        });
                    }
                })
                .catch((error: Error) => {
                    log.error("failed to keep session alive", error);
                    this.eventBus.publish("shell.session.prolongation", {
                        status: "stopped",
                        reason: "error",
                        error: error,
                    });
                });
        }, secondsFromNow * 1000);
    }

    cancelProlongation() {
        if (this.prolongationTimer) window.clearTimeout(this.prolongationTimer);
        this.prolongationTimer = undefined;
    }
}

function withCSRFToken() {

    let options : any = {};

    const token = window.lucom.ip.getCSRFToken ();
    if (typeof token !== "undefined")
        options.headers = { "X-CSRF-TOKEN": token }

    return options;
}

export default SessionSync;
